import { injectable } from 'inversify';
import Mime from 'mime/lite';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { ObjectManager } from '../../../concepts/objects';
import { Point } from '../../../platform/geom/Point';
import { Rect } from '../../../platform/geom/Rect';
import { resolve } from '../../Container';
import { Sticky } from '../../packages/paper/Sticky';
import { Camera } from '../Camera';
import { place } from '../commands/OrderCommands';
import { select } from '../commands/SelectionCommands';
import { RelativeTo } from '../entities/Physical';
import { Detect } from './ContentDetect';
import { ContentPayload } from './ContentPayload';
import { ContentProcessor } from './ContentProcessor';

@injectable()
export class TextDataProcessor implements ContentProcessor {

    public priority = 10;

    public readonly supportedTypes = Object.freeze([
        'text/plain',
    ]);

    @resolve(Camera) private camera!: Camera;
    @resolve(CommandDispatcher) private dispatch!: CommandDispatcher;
    @resolve(ObjectManager) private objects!: ObjectManager;

    public async process(payload: ContentPayload): Promise<boolean | ContentPayload> {
        const { metadata } = payload;

        const textData = await payload.getDataAsText();
        if (!textData) return false;

        // If force set, another detect has declared this text, so just process as text
        if (!metadata.force) {
            // Detect JSON...
            const maybeJson = Detect.json(textData);
            if (maybeJson) {
                return ContentPayload.derive(payload, Mime.getType('json'), textData);
            }
            // Detect SVG...
            const maybeSvg = Detect.svg(textData);
            if (maybeSvg) {
                return ContentPayload.derive(payload, Mime.getType('svg'), textData);
            }
            // Detect CSV...
            const maybeCsv = Detect.csv(textData);
            if (maybeCsv) {
                return ContentPayload.derive(payload, Mime.getType('csv'), textData, maybeCsv);
            }
        }

        // If text, convert to stickies per line (unless line is longer than 140 chars)
        this.processTextIntoStickies(textData);

        return true;
    }

    private processTextIntoStickies(text: string) {
        const { camera, dispatch, objects } = this;

        // For multi-paste (later)
        const stackOffset = Point.zero;

        const lines = text.split(/\n/g).map(x => x.trim()).filter(x => !!x);
        if (!lines.length) return;

        const grid = (i, t) => Point.from({
            x: i % Math.floor(Math.sqrt(t)),
            y: Math.floor(i / Math.floor(Math.sqrt(t))),
        });

        objects.update(u => {

            const root = objects.getRoot();

            const stickies = lines.map((ln, i) => {
                const xy = grid(i, lines.length).multiply(Sticky.Size + 10).add(stackOffset);
                const sk = u.create(Sticky);
                sk.content = ln;
                sk.moveTo(xy);
                return sk;
            });

            const offset = (camera.position).subtract(
                Rect.fromMany(stickies.map(x => x.bounds(RelativeTo.Model)))
                    .size()
                    .divide(2)
            );

            for (const sk of stickies) {
                sk.moveBy(offset);
                root.add(sk);
            }

            dispatch(place, [stickies.map(x => x.id)]);
            dispatch(select, [stickies.map(x => x.id)]);

        });
    }
}
